import type { User } from '@/types'

/**
 * Owner eligibility for Accounts, Contacts, and Opportunities.
 *
 * The "Sales" role is `user_type === 'User'` (it displays as "Sales" via
 * userTypeDisplay.ts). Records may only be newly assigned to an ACTIVE sales rep —
 * non-sales functional roles (Admin, Super Admin, Marketing, Orders, Quotes, RFP,
 * Support) and inactive users are excluded from the picker.
 */
export function isActiveSalesRep(u: Pick<User, 'user_type' | 'is_active'>): boolean {
  return u.user_type === 'User' && u.is_active === true
}

/**
 * Options for an Owner picker: every active sales rep, plus the record's current
 * owner (so editing never drops an existing owner who happens to be an admin,
 * inactive, or another role), sorted by name.
 */
export function ownerPickerOptions<T extends Pick<User, 'id' | 'name' | 'user_type' | 'is_active'>>(
  users: T[],
  currentOwnerId?: string | null
): T[] {
  return users
    .filter((u) => isActiveSalesRep(u) || (!!currentOwnerId && u.id === currentOwnerId))
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
}
